import { useEffect, useMemo, useRef, useState } from "react";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import NoTableEntries from "@components/DataTable/NoTableEntries";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import { useCheckProposedAgency } from "@features/vessel-call/vessel-call-status/hooks/useCheckProposedAgency.ts";
import AddPortServiceButton from "@features/vessel-call/vessel-call-status/PortServices/components/AddPortServiceButton";
import { canUserCancelService } from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import { getPortServiceFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import withDrawers, {
  type DrawerInjectedProps,
} from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import {
  getPortServiceCategoryLabel,
  getPortServiceStatusInfo,
  STATUS_KEYS,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { VESSEL_CALL_RESTRICTED_STATUSES } from "@features/vessel-call/vessel-call-status/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { PortService } from "@services/port-services/types";
import { getPortServices } from "@services/portServicesApi";
import { type PortServiceCategory } from "@services/reference-data/types";
import {
  getPortServiceSubtypes,
  getPortServiceTypes,
  REFERENCE_DATA_TYPES,
} from "@services/referenceDataApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  buildFilterBodyFromUI,
  getMainVessel,
  getPortServicesFilterFields,
  getSecondaryVessel,
  PORT_SERVICES_FIELDS,
} from "./utils";

const LANG_BASE = "vessel-call.port-services";
const LOCAL_STORAGE_PAGE_KEY = "port-services-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "port-services-page-size";
const service = "bff-vessel-call";
const dateTimeFields = ["rts", "ats", "atc"];
const MAIN_PAGE = "main";
const VESSEL_CALL_PAGE = "vessel-call";
const PORT_SERVICES_PAGE_SIZE_MAIN = [25, 50, 100];
const PORT_SERVICES_PAGE_SIZE_VESSEL_CALL = [5, 10, 15, 20, 25];
const CANCEL_DISABLED_STATUSES = [
  STATUS_KEYS.COMPLETED,
  STATUS_KEYS.CLOSED,
  STATUS_KEYS.CANCELLED,
  STATUS_KEYS.REJECTED,
];

const getSearchInquiryContext = (isPage: boolean) => ({
  module: "vessel_call",
  screen: isPage ? "Port Services" : "Vessel Call Port Services",
  action: "search_port_services",
  entityType: "port_service",
});

type Header = {
  field: string;
  columnName: string;
  minWidth?: number;
};

export interface PortServicesData {
  items: PortService[];
  totalElements: number;
}

interface PortServicesProps extends Partial<DrawerInjectedProps> {
  isPage?: boolean;
}
const PortServices = ({ isPage = false, openCancelDrawer = () => {} }: PortServicesProps) => {
  const searchInquiryId = isPage ? "port-services.main.search" : "port-services.vessel-call.search";
  const storePagination = isPage ? MAIN_PAGE : VESSEL_CALL_PAGE;
  const localStoragePageKey = `${LOCAL_STORAGE_PAGE_KEY}-${storePagination}`;
  const localStoragePageSizeKey = `${LOCAL_STORAGE_PAGE_SIZE_KEY}-${storePagination}`;

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(localStoragePageKey);
    return saved ? parseInt(saved, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(localStoragePageSizeKey);
    return saved ? parseInt(saved, 10) : 25;
  });

  const pageSizeOptions = isPage
    ? PORT_SERVICES_PAGE_SIZE_MAIN
    : PORT_SERVICES_PAGE_SIZE_VESSEL_CALL;

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = isPage
      ? localStorage.getItem("port-services-filters-page")
      : localStorage.getItem("port-services-submodule-filters");
    return saved ? JSON.parse(saved) : [];
  });

  const { options: portServiceCategories } = useFormattedReference<PortServiceCategory>(
    REFERENCE_DATA_TYPES.PORT_SERVICE_CATEGORIES,
    "bff-vessel-call",
  );
  const { data: portServiceTypes = [] } = useQuery({
    queryKey: ["portServiceTypes"],
    queryFn: () => getPortServiceTypes("bff-vessel-call"),
  });

  const { data: portServiceSubtypes = [] } = useQuery({
    queryKey: ["portServiceSubtypes"],
    queryFn: () => getPortServiceSubtypes("bff-vessel-call"),
  });

  const { t } = useTranslation();
  const { vesselCallId, vesselCallNumber, vesselCallStatus } =
    useOutletContext<{
      vesselCallId: string;
      vesselCallNumber: string;
      vesselCallStatus: string;
    }>() || {};
  let columnsToExcludeFromGrid: string[] = ["id"];

  if (!isPage) {
    columnsToExcludeFromGrid = [...columnsToExcludeFromGrid, "vesselCallNo", "imo"];
  }
  const filtersBody = buildFilterBodyFromUI(filters);
  const {
    data: portServicesData,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    dataUpdatedAt,
    error,
  } = useQuery<PortServicesData>({
    queryKey: ["getPortServices", page, pageSize, filters, isPage, vesselCallId],
    queryFn: () => {
      return getPortServices({
        service,
        page: page - 1,
        pageSize,
        filters: {
          vesselCallId: !isPage ? vesselCallId : undefined,
          vesselCallNo: !isPage ? [vesselCallNumber] : undefined,
          ...filtersBody,
        },
      });
    },
    enabled: isPage || !!vesselCallId,
    // Options to re-fetch on every mount:
    staleTime: 0,
    refetchOnMount: true,
  });
  useTrackPendingInquiry(searchInquiryId, {
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (!isPage || isLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Port Services");
    trackScreenLoad("Vessel Call Management", "Port Services", startedAt.current);
  }, [error, isLoading, isPage]);

  useEffect(() => {
    if (!isLoading && portServicesData && portServicesData.items.length === 0 && page > 1) {
      setPage(1);
      localStorage.setItem(localStoragePageKey, "1");
    }
  }, [portServicesData, isLoading]);

  const totalPortServicesCount = portServicesData?.totalElements || 0;
  const { user, userActionPermissions, isShippingAgentUser } = useAuthStore();
  const pagedRows = useMemo(() => {
    const portServicesRows =
      portServicesData?.items.map((item) => ({
        ...item,
        rawStatus: item.status,
        rawServiceCategory: item.serviceCategory,
        status: getPortServiceStatusInfo(item.status, item.serviceCategory).label,
        badgeType: getPortServiceStatusInfo(item.status, item.serviceCategory).badgeType,
        serviceCategory: getPortServiceCategoryLabel(item.serviceCategory),
        vesselName: getMainVessel(item.linkedVesselCalls)?.vesselName,
        vesselCallNo: getMainVessel(item.linkedVesselCalls)?.vesselCallNo,
        vesselCallId: getMainVessel(item.linkedVesselCalls)?.vesselCallId,
        imo: getMainVessel(item.linkedVesselCalls)?.imo,
        secondaryVesselCallId: getSecondaryVessel(item.linkedVesselCalls)?.vesselCallId,
      })) || [];
    return portServicesRows;
  }, [portServicesData]);

  const headers: Header[] = useMemo(
    () =>
      Object.keys(PORT_SERVICES_FIELDS)
        .filter((key) => !columnsToExcludeFromGrid.includes(key))
        .map((key) => ({
          field: key.toString(),
          columnName: PORT_SERVICES_FIELDS[key as keyof typeof PORT_SERVICES_FIELDS],
          minWidth: 52,
          sortable: false,
          type: dateTimeFields.includes(String(key)) ? "dateTime" : key,
        })),
    [],
  );

  const availableFields = useMemo(
    () =>
      getPortServicesFilterFields(
        portServiceCategories,
        portServiceTypes,
        portServiceSubtypes,
        isPage,
      ),
    [portServiceCategories, portServiceTypes, portServiceSubtypes, isPage],
  );

  const onApplyFilters = (f: FilterItem[]) => {
    startInquiryTiming(searchInquiryId, getSearchInquiryContext(isPage));
    setFilters(f);
    if (isPage) {
      localStorage.setItem("port-services-filters-page", JSON.stringify(f));
    } else localStorage.setItem("port-services-submodule-filters", JSON.stringify(f));
    setPage(1);
    localStorage.setItem(localStoragePageKey, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(searchInquiryId, getSearchInquiryContext(isPage));
    }
    setFilters([]);
    if (isPage) {
      localStorage.removeItem("port-services-filters-page");
    } else {
      localStorage.removeItem("port-services-submodule-filters");
    }
  };

  const ownersMatter = !isPage ? useCheckOwnersMatter(vesselCallId) : false;
  const proposedAgency = !isPage ? useCheckProposedAgency(vesselCallId) : false;

  const userHasRequestPermission =
    checkPermission(userActionPermissions, "canRequestPortService") &&
    !ownersMatter &&
    !proposedAgency;

  const hasRestrictedVesselCallStatus = Object.values(VESSEL_CALL_RESTRICTED_STATUSES).some(
    (status) => status === vesselCallStatus,
  );

  const userCanCancelPortService = checkPermission(userActionPermissions, "canCancelPortService");
  const currentUserRoles = user?.roles ?? [];
  const isShippingAgent = currentUserRoles.includes("shipping-agent");

  const isCancelActionEnabled = (row: (typeof pagedRows)[number]) => {
    const currentStatus = row.rawStatus ?? row.status;
    const flow = getPortServiceFlow(
      row.rawServiceCategory ?? row.serviceCategory ?? "",
      row.serviceType ?? "",
      row.serviceSubType ?? "",
      row.needsOperationsApproval,
    );
    const isSameCompany = user?.companyId === getMainVessel(row.linkedVesselCalls)?.companyId;

    return (
      canUserCancelService(currentUserRoles, userCanCancelPortService, currentStatus, flow) ||
      (isShippingAgent && isSameCompany && !CANCEL_DISABLED_STATUSES.includes(currentStatus))
    );
  };

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  return portServicesData?.items.length || filters.length ? (
    <>
      {!isPage && userHasRequestPermission && (
        <div className="flex justify-end">
          <AddPortServiceButton
            langBase={LANG_BASE}
            hasRestrictedVesselCallStatus={hasRestrictedVesselCallStatus}
          />
        </div>
      )}
      <CustomDataTable
        testId="port-services-table"
        title={t(`${LANG_BASE}.title`)}
        actionName={t(`${LANG_BASE}.addVesselCall`)}
        headers={headers}
        rows={pagedRows}
        page={page}
        pageSize={pageSize}
        totalCount={totalPortServicesCount}
        canPreview={true}
        onPageChange={(newPage, newSize) => {
          if (newPage !== page || newSize !== pageSize) {
            startInquiryTiming(searchInquiryId, getSearchInquiryContext(isPage));
          }
          setPage(newPage);
          setPageSize(newSize);
          localStorage.setItem(localStoragePageKey, String(newPage));
          localStorage.setItem(localStoragePageSizeKey, String(newSize));
        }}
        actionColumn={
          userCanCancelPortService
            ? {
                actionTitle: t(`${LANG_BASE}.cancel-button`),
                onActionClick: (row) =>
                  openCancelDrawer({
                    ...row,
                    status: row.rawStatus ?? row.status,
                  }),
                isActionEnabled: isCancelActionEnabled,
              }
            : undefined
        }
        interModulesNavigation={
          isPage
            ? (row) => {
                const secondaryVessel = getSecondaryVessel(row.linkedVesselCalls);
                const mainVessel = getMainVessel(row.linkedVesselCalls);

                let showSecondaryVesselCallLink = false;
                let isSameCompanyId = false;
                let isSubmiitedBy = false;
                if (secondaryVessel) {
                  showSecondaryVesselCallLink = user?.companyId === secondaryVessel.companyId;
                  isSameCompanyId = mainVessel?.companyId === secondaryVessel.companyId;
                  isSubmiitedBy = row.submittedBy === user?.name;
                }
                if (
                  isShippingAgentUser &&
                  ((showSecondaryVesselCallLink && !isSameCompanyId) ||
                    (isSameCompanyId && !isSubmiitedBy))
                ) {
                  return {
                    pathToModule: `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}`,
                    targetModuleResourceBy: "secondaryVesselCallId",
                    pathToSubModule: `${paths.vesselCall.portServices}`,
                    targetSubModuleResourceBy: "id",
                  };
                } else {
                  return {
                    pathToModule: `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}`,
                    targetModuleResourceBy: "vesselCallId",
                    pathToSubModule: `${paths.vesselCall.portServices}`,
                    targetSubModuleResourceBy: "id",
                  };
                }
              }
            : null
        }
        navigationLinkBase=""
        navigationParam="id"
        pageSizeOptions={pageSizeOptions}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        availableFields={availableFields}
      />
    </>
  ) : (
    <>
      <NoTableEntries
        noMargin
        customMessagePrefix={`${LANG_BASE}.empty-table-title`}
        dataName={t("vessel-call.vessel-status.port-services")}
        actionName={userHasRequestPermission && !isPage ? t(`${LANG_BASE}.add-service`) : ""}
        descriptionComponents={{ strong: <strong /> }}
      />
      {userHasRequestPermission && !isPage && (
        <div className="flex justify-center">
          <AddPortServiceButton
            langBase={LANG_BASE}
            hasRestrictedVesselCallStatus={hasRestrictedVesselCallStatus}
          />
        </div>
      )}
    </>
  );
};

export default withDrawers(PortServices as unknown as React.ComponentType<any>);
